import { useState } from 'react';
import { usePlanosContext } from '../Context';
import { PlanosService } from '../Service';
import { useParamsContext } from '../../../../contexts/ParamsContext';
import { useAppContext } from '../../../../contexts/AppContext';
import {
    formatPlanPrice,
    formatServiceRecurrence,
    getPlanBillingLabel,
    getPlanCoverUrl,
    getPlanPriceSuffix,
} from '../../cliente/planosUtils';
import '../../Planos.css';
import { ChevronRight, Layers, TriangleAlert } from 'lucide-react';

const service = new PlanosService();

type StatusFilter = 'all' | 'active' | 'inactive';

const PlanoRow = ({ plano }: { plano: any }) => {
    const { setParams } = useParamsContext();
    const { reloadData } = usePlanosContext();
    const { errorSnackbar } = useAppContext();
    const [updating, setUpdating] = useState(false);
    const isActive = plano.status === 1;
    const coverUrl = getPlanCoverUrl(plano);
    const services: any[] = Array.isArray(plano.services) ? plano.services : [];
    const servicesLabel = services
        .map(
            (item) =>
                `${Number(item.quantity || 1)}x ${item.serviceName || item.service?.name || 'Serviço removido'} ${formatServiceRecurrence(item.recurrence)}`,
        )
        .join(', ');

    const toggleStatus = async () => {
        try {
            setUpdating(true);
            await service.update(plano._id, { status: isActive ? 0 : 1 });
            await reloadData();
        } catch (e) {
            console.error(e);
            errorSnackbar(`Não foi possível ${isActive ? 'desativar' : 'ativar'} o plano.`);
        } finally {
            setUpdating(false);
        }
    };

    return (
        <li className={`pla-row ${isActive ? '' : 'is-inactive'}`}>
            <button
                type="button"
                className="pla-row-main"
                onClick={() => setParams({ action: 'edit', id: plano._id })}
                aria-label={`Editar ${plano.name}`}
            >
                <span className="pla-thumb">
                    {coverUrl ? (
                        <img src={coverUrl} alt="" loading="lazy" />
                    ) : (
                        <Layers aria-hidden />
                    )}
                </span>
                <span className="pla-row-name">
                    <span className="pla-row-title">{plano.name}</span>
                    {!isActive && (
                        <span className="sf-tag" data-tone="gray">
                            Desativado
                        </span>
                    )}
                    {plano.allowSelfSubscription === false && (
                        <span className="sf-tag" data-tone="gray">
                            Só pela equipe
                        </span>
                    )}
                </span>
                <span className="pla-row-services" title={servicesLabel}>
                    {servicesLabel || 'Sem serviços'}
                </span>
                <span className="pla-row-billing">{getPlanBillingLabel(plano)}</span>
                <span className="pla-row-price">
                    {formatPlanPrice(plano.price)}
                    <span>{getPlanPriceSuffix(plano.periodicity)}</span>
                </span>
                <ChevronRight className="pla-row-chevron" aria-hidden />
            </button>
            <input
                type="checkbox"
                className="toggle toggle-success toggle-sm pla-row-toggle"
                checked={isActive}
                onChange={toggleStatus}
                disabled={updating}
                aria-label={`Plano ${plano.name} ativo`}
                title={isActive ? 'Desativar plano' : 'Ativar plano'}
            />
        </li>
    );
};

export default function PlanosList() {
    const { data, loading, error, reloadData } = usePlanosContext();
    const { setParams } = useParamsContext();
    const [filter, setFilter] = useState<StatusFilter>('all');

    if (loading) {
        return (
            <div className="space-y-2 px-2" aria-busy="true">
                {[0, 1, 2, 3].map((item) => (
                    <div key={item} className="sf-skeleton h-16" />
                ))}
            </div>
        );
    }

    if (error && !data.length) {
        return (
            <div className="sf-state">
                <TriangleAlert aria-hidden />
                <h2>Não foi possível carregar os planos</h2>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => void reloadData()}>
                    Tentar novamente
                </button>
            </div>
        );
    }

    if (!data.length) {
        return (
            <div className="sf-state">
                <Layers aria-hidden />
                <h2>Nenhum plano criado</h2>
                <p>Crie planos com serviços incluídos para vender assinaturas aos seus clientes.</p>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setParams({ action: 'add' })}>
                    Criar plano
                </button>
            </div>
        );
    }

    const activeCount = data.filter((plano) => plano.status === 1).length;
    const inactiveCount = data.length - activeCount;
    const visible = data.filter((plano) => {
        if (filter === 'active') return plano.status === 1;
        if (filter === 'inactive') return plano.status !== 1;
        return true;
    });

    return (
        <div className="pla-page pla-list px-2">
            {inactiveCount > 0 && activeCount > 0 && (
                <div className="pla-toolbar">
                    <div className="sf-segmented" role="group" aria-label="Filtrar planos">
                        <button type="button" aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>
                            Todos
                            <span className="sf-segmented-count">{data.length}</span>
                        </button>
                        <button type="button" aria-pressed={filter === 'active'} onClick={() => setFilter('active')}>
                            Ativos
                            <span className="sf-segmented-count">{activeCount}</span>
                        </button>
                        <button type="button" aria-pressed={filter === 'inactive'} onClick={() => setFilter('inactive')}>
                            Inativos
                            <span className="sf-segmented-count">{inactiveCount}</span>
                        </button>
                    </div>
                </div>
            )}

            <div className="pla-list-head" aria-hidden>
                <span>Plano</span>
                <span>Serviços incluídos</span>
                <span>Cobrança</span>
                <span className="text-right">Valor</span>
            </div>
            <ul className="sf-group">
                {visible.map((plano) => (
                    <PlanoRow key={plano._id} plano={plano} />
                ))}
            </ul>
        </div>
    );
}
